import Service from "../service";

/**
 * Global Search API wrapper (server: /global-search).
 *
 * Same convention as actionHubApi.js: the server envelope
 * `{ status: 200 | 403, data, message }` (configs.success_status / configs.error_status)
 * is returned untouched so the caller can check `status` and read `message` on failure.
 *
 * Expected `data` shape (confirmed with backend):
 * {
 *   employees: { count, items: [{ _id, name, email, employeeId }] },
 *   leaveRequests: { count, items: [{ _id, employeeName, leaveType, fromDate, toDate, status }] },
 *   attendanceRecords: { count, items: [{ _id, employeeName, date, status }] }
 * }
 */

// QA found a retry after a transient failure could sit on "Searching..."
// indefinitely with no recovery - there was no client-side give-up anywhere.
// Service.makeAPICall's GET path (see client/src/service/index.js) doesn't
// expose a per-call axios `timeout` override (only `axios.defaults.timeout`
// of 2 MINUTES applies, far too long for search-as-you-type), so the
// give-up is enforced here instead via a race against a plain timer -
// deliberately NOT touching the shared Service layer every other GET call
// in the app relies on (caching/dedup) for an unrelated search bugfix.
const SEARCH_TIMEOUT_MS = 10000; // 10s - reasonable for search-as-you-type

const globalSearchApi = {
  /** Server-side search across employees, leave requests and attendance records. params: { q } */
  search: async (q) => {
    // Never rejects - makeAPICall's GET branch already catches internally and
    // returns `error.response` (possibly undefined), normalized below to the
    // same envelope shape the timeout branch resolves to, so whichever side
    // of the race wins, the caller (useGlobalSearch) sees one consistent
    // shape and its existing `response?.status !== 200` check handles both.
    const apiCallPromise = Service.makeAPICall({ methodName: Service.getMethod, api_url: "/global-search", body: { q } }).then(
      (response) => response?.data || { status: 403, data: null, message: "No response from server" }
    );
    let timer;
    const timeoutPromise = new Promise((resolve) => {
      timer = setTimeout(
        () => resolve({ status: 403, data: null, message: "Search timed out. Please try again." }),
        SEARCH_TIMEOUT_MS
      );
    });
    const result = await Promise.race([apiCallPromise, timeoutPromise]);
    clearTimeout(timer);
    return result;
  },
};

export default globalSearchApi;
